/**
 * 剧本监测 · 剧本解析
 * 1) 加载全部工作表 → 从标题解析日期 → 日历展示
 * 2) 点击日历日期或列表选中后，解析该日剧本并用弹窗展示
 */

import { useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  App,
  Button,
  Calendar,
  Card,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { FileTextOutlined, ReloadOutlined } from "../components/icons";
import { buildFeishuMediaProxyUrl } from "../../shared/feishuCellValue";
import {
  DISPATCH_SCRIPT_CONTENT_TYPE_LABELS,
  DISPATCH_SCRIPT_ROLE_KIND_LABELS,
  DISPATCH_SCRIPT_SLOT_LABELS,
  type DispatchScriptContentType,
  type DispatchScriptSkipRow,
  type DispatchScriptSlotKind,
  type ParsedDispatchScriptRow,
} from "../../shared/dispatchScriptSheet";
import type {
  AnnotatedSheetTitleDate,
  SheetTitleDateIndex,
} from "../../shared/dispatchScriptSheetTitleDate";
import { trpc } from "../trpc";

dayjs.locale("zh-cn");

const { Text, Paragraph } = Typography;

interface SheetOption {
  sheetId: string;
  title: string;
  index: number;
  hidden: boolean;
  rowCount: number;
  columnCount: number;
}

interface ParsePreviewResult {
  spreadsheetToken: string;
  sheetId: string;
  sheetTitle: string;
  range: string;
  sheets: SheetOption[];
  rows: ParsedDispatchScriptRow[];
  skipped: DispatchScriptSkipRow[];
  stats: {
    totalRows: number;
    keptRows: number;
    skippedRows: number;
    dispatcherRows: number;
    atmosphereRows: number;
    rowsWithTime: number;
    rowsWithInheritedTime: number;
    rowsWithTimeParseError: number;
    contentTypeCounts: Record<DispatchScriptContentType, number>;
    slotKindCounts: Record<DispatchScriptSlotKind, number>;
    rowsWithUnrecognized: number;
  };
}

const CONTENT_TYPE_COLORS: Record<DispatchScriptContentType, string> = {
  text: "blue",
  image: "green",
  video: "purple",
  sticker: "orange",
  slot: "magenta",
};

function contentTypeTag(type: DispatchScriptContentType) {
  return (
    <Tag key={type} color={CONTENT_TYPE_COLORS[type]}>
      {DISPATCH_SCRIPT_CONTENT_TYPE_LABELS[type]}
    </Tag>
  );
}

export function DispatchScriptParsePage() {
  const { message } = App.useApp();
  const [url, setUrl] = useState("");
  const [sheetId, setSheetId] = useState<string | undefined>();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetOption[]>([]);
  const [dateIndex, setDateIndex] = useState<SheetTitleDateIndex | null>(null);
  const [calendarValue, setCalendarValue] = useState(() => dayjs());
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState<ParsePreviewResult | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);

  const sheetsByDate = useMemo(() => {
    const map = new Map<string, AnnotatedSheetTitleDate[]>();
    if (!dateIndex) return map;
    for (const day of dateIndex.byDate) {
      map.set(day.date, day.sheets);
    }
    return map;
  }, [dateIndex]);

  const unmatchedSheets = useMemo(
    () => dateIndex?.sheets.filter((sheet) => !sheet.dateParse.ok) ?? [],
    [dateIndex]
  );

  const sheetOptions = useMemo(
    () =>
      (dateIndex?.sheets ?? sheets).map((sheet) => {
        const annotated = sheet as SheetOption & {
          dateParse?: AnnotatedSheetTitleDate["dateParse"];
        };
        const dateLabel = annotated.dateParse
          ? annotated.dateParse.ok
            ? annotated.dateParse.result.date
            : "无法判断日期"
          : "";
        return {
          value: annotated.sheetId,
          label: `${annotated.title}${annotated.hidden ? "（隐藏）" : ""}${
            dateLabel ? ` · ${dateLabel}` : ""
          }`,
        };
      }),
    [dateIndex, sheets]
  );

  const selectedDateLabel = useMemo(() => {
    if (selectedDate) return selectedDate;
    if (!dateIndex || !sheetId) return null;
    const sheet = dateIndex.sheets.find((item) => item.sheetId === sheetId);
    if (sheet?.dateParse.ok) return sheet.dateParse.result.date;
    return null;
  }, [dateIndex, selectedDate, sheetId]);

  const applySheetSelection = (nextSheetId: string | undefined, dateKey?: string | null) => {
    setSheetId(nextSheetId);
    setResult(null);
    setResultModalOpen(false);
    if (dateKey !== undefined) {
      setSelectedDate(dateKey);
      return;
    }
    if (!nextSheetId || !dateIndex) {
      setSelectedDate(null);
      return;
    }
    const sheet = dateIndex.sheets.find((item) => item.sheetId === nextSheetId);
    setSelectedDate(sheet?.dateParse.ok ? sheet.dateParse.result.date : null);
  };

  const loadSheets = async () => {
    if (!url.trim()) {
      message.warning("请先填写飞书表格链接");
      return;
    }
    setLoadingSheets(true);
    try {
      const data = await trpc.dispatchScript.listSheetsByUrl.mutate({
        url: url.trim(),
      });
      setSheets(data.sheets);
      setDateIndex(data.dateIndex);
      setResult(null);
      setResultModalOpen(false);
      setSelectedDate(null);

      if (data.dateIndex.primaryYearMonth) {
        setCalendarValue(dayjs(`${data.dateIndex.primaryYearMonth}-01`));
      }

      const preferred =
        data.sheets.find((sheet) => !sheet.hidden)?.sheetId ?? data.sheets[0]?.sheetId;
      if (preferred) {
        const preferredSheet = data.dateIndex.sheets.find((item) => item.sheetId === preferred);
        setSheetId(preferred);
        setSelectedDate(
          preferredSheet?.dateParse.ok ? preferredSheet.dateParse.result.date : null
        );
      } else {
        setSheetId(undefined);
      }

      message.success(
        `已加载 ${data.sheets.length} 个工作表：匹配日期 ${data.dateIndex.matchedCount}，无法判断 ${data.dateIndex.unmatchedCount}`
      );
    } catch (error: any) {
      message.error(error?.message || "加载工作表失败");
    } finally {
      setLoadingSheets(false);
    }
  };

  const selectDateOnCalendar = (date: Dayjs) => {
    setCalendarValue(date);
    const key = date.format("YYYY-MM-DD");
    const daySheets = sheetsByDate.get(key) ?? [];
    if (daySheets.length === 0) {
      setSelectedDate(key);
      setSheetId(undefined);
      setResult(null);
      setResultModalOpen(false);
      message.info(`${key} 没有匹配到工作表`);
      return;
    }
    const keepCurrent = daySheets.some((sheet) => sheet.sheetId === sheetId);
    const firstSheet = daySheets[0];
    if (!firstSheet) return;
    const nextSheetId = keepCurrent && sheetId ? sheetId : firstSheet.sheetId;
    applySheetSelection(nextSheetId, key);
  };

  const handleParse = async (overrideSheetId?: string) => {
    if (!url.trim()) {
      message.warning("请先填写飞书表格链接");
      return;
    }
    const targetSheetId = overrideSheetId ?? sheetId;
    if (!targetSheetId) {
      message.warning("请先在日历上选择有工作表的日期");
      return;
    }
    setParsing(true);
    try {
      const data = await trpc.dispatchScript.parseByUrl.mutate({
        url: url.trim(),
        sheetId: targetSheetId,
      });
      setResult(data);
      setSheets(data.sheets);
      setSheetId(data.sheetId);
      setResultModalOpen(true);
      message.success(`解析完成：保留 ${data.stats.keptRows} 行，跳过 ${data.stats.skippedRows} 行`);
    } catch (error: any) {
      message.error(error?.message || "解析失败");
    } finally {
      setParsing(false);
    }
  };

  const calendarCellRender = (current: Dayjs, info: { type: string; originNode: ReactNode }) => {
    if (info.type !== "date") return info.originNode;
    const key = current.format("YYYY-MM-DD");
    const daySheets = sheetsByDate.get(key) ?? [];
    const isSelected = selectedDateLabel === key;
    return (
      <div
        style={{
          minHeight: 72,
          padding: "0 2px",
          borderRadius: 4,
          outline: isSelected ? "2px solid #1677ff" : undefined,
          outlineOffset: -2,
          background: isSelected ? "rgba(22, 119, 255, 0.06)" : undefined,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{current.date()}</div>
        {daySheets.map((sheet) => (
          <div
            key={sheet.sheetId}
            title={sheet.title}
            onClick={(event) => {
              event.stopPropagation();
              setCalendarValue(current);
              applySheetSelection(sheet.sheetId, key);
            }}
            style={{
              fontSize: 11,
              lineHeight: 1.3,
              color: sheet.sheetId === sheetId ? "#1677ff" : "#334155",
              background: sheet.sheetId === sheetId ? "#e6f4ff" : "#f1f5f9",
              borderRadius: 3,
              padding: "1px 4px",
              marginBottom: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {sheet.title}
          </div>
        ))}
      </div>
    );
  };

  const annotatedColumns: ColumnsType<AnnotatedSheetTitleDate> = [
    {
      title: "标题",
      dataIndex: "title",
      render: (title: string, record) => (
        <Space size={4} wrap>
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() => {
              applySheetSelection(
                record.sheetId,
                record.dateParse.ok ? record.dateParse.result.date : null
              );
              if (record.dateParse.ok) {
                setCalendarValue(dayjs(record.dateParse.result.date));
              }
            }}
          >
            {title}
          </Button>
          {record.hidden ? <Tag>隐藏</Tag> : null}
        </Space>
      ),
    },
    {
      title: "解析日期",
      key: "date",
      width: 160,
      render: (_, record) =>
        record.dateParse.ok ? (
          <Space size={4}>
            <Text strong>{record.dateParse.result.date}</Text>
            {record.dateParse.result.yearAssumed ? <Tag color="gold">年份补全</Tag> : null}
          </Space>
        ) : (
          <Text type="danger">无法判断</Text>
        ),
    },
    {
      title: "命中片段 / 原因",
      key: "detail",
      render: (_, record) =>
        record.dateParse.ok ? (
          <Text type="secondary">
            「{record.dateParse.result.matchedText}」· {record.dateParse.result.pattern}
          </Text>
        ) : (
          <Text type="secondary">{record.dateParse.reason}</Text>
        ),
    },
    {
      title: "操作",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          loading={parsing && sheetId === record.sheetId}
          onClick={() => {
            applySheetSelection(
              record.sheetId,
              record.dateParse.ok ? record.dateParse.result.date : null
            );
            if (record.dateParse.ok) {
              setCalendarValue(dayjs(record.dateParse.result.date));
            }
            void handleParse(record.sheetId);
          }}
        >
          解析日期
        </Button>
      ),
    },
  ];

  const rowColumns: ColumnsType<ParsedDispatchScriptRow> = [
    {
      title: "行号",
      dataIndex: "rowNumber",
      width: 72,
      fixed: "left",
    },
    {
      title: "时间",
      key: "time",
      width: 140,
      render: (_, record) => {
        if (record.time.parseError) {
          return (
            <Space orientation="vertical" size={0}>
              <Text type="danger">{record.time.raw || "—"}</Text>
              <Text type="danger" style={{ fontSize: 12 }}>
                {record.time.parseError}
              </Text>
            </Space>
          );
        }
        if (!record.time.display) {
          return <Text type="secondary">未标注</Text>;
        }
        return (
          <Space size={4}>
            <Text strong>{record.time.display}</Text>
            {record.time.inherited ? <Tag>继承</Tag> : null}
            {record.time.raw && !record.time.inherited ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({record.time.raw})
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "角色",
      key: "role",
      width: 160,
      render: (_, record) => (
        <Space size={4} wrap>
          <Tag color={record.roleKind === "dispatcher" ? "geekblue" : "cyan"}>
            {DISPATCH_SCRIPT_ROLE_KIND_LABELS[record.roleKind]}
          </Tag>
          <Text>{record.role}</Text>
          {record.roleIsInherited ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              继承
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "内容类型",
      dataIndex: "contentTypes",
      width: 200,
      render: (types: DispatchScriptContentType[], record) => (
        <Space size={[4, 4]} wrap>
          {types.length ? types.map(contentTypeTag) : <Text type="secondary">无</Text>}
          {record.slots.map((slot) => (
            <Tag key={slot} color="magenta">
              {DISPATCH_SCRIPT_SLOT_LABELS[slot]}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "内容",
      key: "content",
      render: (_, record) => (
        <div>
          {record.slots.length > 0 ? (
            <div style={{ marginBottom: record.text || record.imageFileToken ? 6 : 0 }}>
              {record.slots.map((slot) => (
                <Tag key={`slot-${slot}`} color="magenta">
                  插槽·{DISPATCH_SCRIPT_SLOT_LABELS[slot]}
                </Tag>
              ))}
            </div>
          ) : null}
          {record.imageFileToken ? (
            <img
              src={buildFeishuMediaProxyUrl(record.imageFileToken)}
              alt=""
              loading="lazy"
              style={{
                display: "block",
                maxWidth: 120,
                maxHeight: 120,
                objectFit: "contain",
                borderRadius: 4,
                marginBottom: record.text ? 6 : 0,
              }}
            />
          ) : null}
          {record.text ? (
            <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{record.text}</span>
          ) : null}
          {!record.imageFileToken && !record.text && record.slots.length === 0 ? (
            <Text type="secondary">—</Text>
          ) : null}
          {record.unrecognizedHints.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              {record.unrecognizedHints.map((hint) => (
                <Alert key={hint} type="warning" showIcon message={hint} style={{ marginBottom: 4 }} />
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  const skippedColumns: ColumnsType<DispatchScriptSkipRow> = [
    { title: "行号", dataIndex: "rowNumber", width: 72 },
    { title: "跳过原因", dataIndex: "reason", width: 280 },
    {
      title: "角色原文",
      dataIndex: "roleRaw",
      width: 120,
      render: (value: string) => value || "—",
    },
    {
      title: "时间原文",
      dataIndex: "timeRaw",
      width: 140,
      render: (value: string) => value || "—",
    },
    {
      title: "内容预览",
      dataIndex: "contentPreview",
      render: (value: string) => value || "—",
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <Card variant="borderless" className="shadow-sm">
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              剧本解析
            </Text>
            <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
              先加载全部工作表，根据标题判断每天对应哪个 Sheet；在日历上点击日期选中后，点「解析选中日期」查看剧本内容。
            </Paragraph>
          </div>

          <Alert
            type="info"
            showIcon
            message="标题日期识别规则"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>支持：7.15、7-15、7月15日、2026-07-15 等（可带周一）</li>
                <li>标题只有月日时，年份固定按今年补全</li>
                <li>纯数字日、分组名等无法准确判断的，会进入未匹配列表</li>
              </ul>
            }
          />

          <Input.TextArea
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://xxx.feishu.cn/sheets/xxxxxx?sheet=yyyy"
            autoSize={{ minRows: 2, maxRows: 4 }}
          />

          <Space wrap>
            <Select
              style={{ minWidth: 280 }}
              placeholder="当前选中的工作表"
              allowClear
              value={sheetId}
              onChange={(value) => applySheetSelection(value)}
              options={sheetOptions}
              showSearch
              optionFilterProp="label"
            />
            <Button type="primary" loading={loadingSheets} onClick={loadSheets}>
              加载全部工作表
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={parsing}
              disabled={!sheetId}
              onClick={() => handleParse()}
            >
              解析选中日期
            </Button>
            {selectedDateLabel ? (
              <Text type="secondary">已选日期：{selectedDateLabel}</Text>
            ) : null}
          </Space>
        </Space>
      </Card>

      {dateIndex ? (
        <>
          <Card variant="borderless" className="shadow-sm" title="工作表日期匹配">
            <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="工作表总数">{dateIndex.sheets.length}</Descriptions.Item>
              <Descriptions.Item label="已匹配日期">{dateIndex.matchedCount}</Descriptions.Item>
              <Descriptions.Item label="无法判断">{dateIndex.unmatchedCount}</Descriptions.Item>
              <Descriptions.Item label="年份补全">
                {dateIndex.yearAssumedCount}
                {dateIndex.defaultYearUsed != null ? `（今年 ${dateIndex.defaultYearUsed}）` : ""}
              </Descriptions.Item>
            </Descriptions>

            {dateIndex.matchedCount === 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="没有从标题解析出任何日期"
                description="请确认标题是否含 7.15 / 7月15日 这类写法后重新加载。"
              />
            ) : null}

            <Calendar
              value={calendarValue}
              onSelect={selectDateOnCalendar}
              onPanelChange={setCalendarValue}
              cellRender={calendarCellRender}
            />
          </Card>

          <Card
            variant="borderless"
            className="shadow-sm"
            title={`全部工作表（${dateIndex.sheets.length}）`}
          >
            <Table
              rowKey={(row) => row.sheetId}
              columns={annotatedColumns}
              dataSource={dateIndex.sheets}
              pagination={{ pageSize: 30, showSizeChanger: true }}
              size="small"
              rowClassName={(record) =>
                record.sheetId === sheetId ? "ant-table-row-selected" : ""
              }
            />
          </Card>

          {unmatchedSheets.length > 0 ? (
            <Card
              variant="borderless"
              className="shadow-sm"
              title={`无法判断日期的工作表（${unmatchedSheets.length}）`}
            >
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="这些标题没有可靠日期，不会出现在日历上"
              />
              <Table
                rowKey={(row) => `unmatched-${row.sheetId}`}
                columns={annotatedColumns}
                dataSource={unmatchedSheets}
                pagination={{ pageSize: 20 }}
                size="small"
              />
            </Card>
          ) : null}
        </>
      ) : null}

      <Modal
        title={
          result
            ? `剧本内容 · ${result.sheetTitle}${selectedDateLabel ? `（${selectedDateLabel}）` : ""}`
            : "剧本内容"
        }
        open={resultModalOpen && Boolean(result)}
        onCancel={() => setResultModalOpen(false)}
        footer={
          <Button type="primary" onClick={() => setResultModalOpen(false)}>
            关闭
          </Button>
        }
        width="min(1100px, 96vw)"
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        destroyOnHidden
      >
        {result ? (
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
              <Descriptions.Item label="工作表">{result.sheetTitle}</Descriptions.Item>
              <Descriptions.Item label="Sheet ID">{result.sheetId}</Descriptions.Item>
              <Descriptions.Item label="Range">{result.range}</Descriptions.Item>
              <Descriptions.Item label="总行数">{result.stats.totalRows}</Descriptions.Item>
              <Descriptions.Item label="保留行">{result.stats.keptRows}</Descriptions.Item>
              <Descriptions.Item label="跳过行">{result.stats.skippedRows}</Descriptions.Item>
              <Descriptions.Item label="发单侧">{result.stats.dispatcherRows}</Descriptions.Item>
              <Descriptions.Item label="气氛员">{result.stats.atmosphereRows}</Descriptions.Item>
              <Descriptions.Item label="自带时间">{result.stats.rowsWithTime}</Descriptions.Item>
              <Descriptions.Item label="继承时间">
                {result.stats.rowsWithInheritedTime}
              </Descriptions.Item>
              <Descriptions.Item label="时间解析失败">
                {result.stats.rowsWithTimeParseError}
              </Descriptions.Item>
              <Descriptions.Item label="待确认内容">
                {result.stats.rowsWithUnrecognized}
              </Descriptions.Item>
              <Descriptions.Item label="类型分布" span={3}>
                <Space wrap>
                  {(Object.keys(result.stats.contentTypeCounts) as DispatchScriptContentType[]).map(
                    (type) => (
                      <Tag key={type} color={CONTENT_TYPE_COLORS[type]}>
                        {DISPATCH_SCRIPT_CONTENT_TYPE_LABELS[type]}{" "}
                        {result.stats.contentTypeCounts[type]}
                      </Tag>
                    )
                  )}
                  {(Object.keys(result.stats.slotKindCounts) as DispatchScriptSlotKind[]).map(
                    (slot) =>
                      result.stats.slotKindCounts[slot] > 0 ? (
                        <Tag key={slot} color="magenta">
                          {DISPATCH_SCRIPT_SLOT_LABELS[slot]} {result.stats.slotKindCounts[slot]}
                        </Tag>
                      ) : null
                  )}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                保留行（{result.rows.length}）
              </Text>
              <Table
                rowKey={(row) => `${row.rowIndex}`}
                columns={rowColumns}
                dataSource={result.rows}
                scroll={{ x: 960 }}
                pagination={{ pageSize: 30, showSizeChanger: true }}
                size="middle"
              />
            </div>

            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                跳过行（{result.skipped.length}）
              </Text>
              {result.skipped.length === 0 ? (
                <Text type="secondary">没有跳过行</Text>
              ) : (
                <Table
                  rowKey={(row) => `skip-${row.rowIndex}`}
                  columns={skippedColumns}
                  dataSource={result.skipped}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  size="small"
                />
              )}
            </div>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
}
